import { encode, decode, getToken as getNextAuthToken, GetTokenParams, JWT } from 'next-auth/jwt';
import { SessionStore } from './lib/cookie';
import { env } from 'lib/env';
import { AnnouncementAudienceTypeValues, AnnouncementAudienceType } from 'lib/util/audiences';
import { Claim, ClaimSet } from './types';
import { logError, logWarning, makeLoggableError } from 'lib/util/errors';
import { NextRequest } from 'next/server';
import { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next';
import { Cookie } from 'next-auth/core/lib/cookie';
import { serialize } from 'cookie';

type RoleStringType = string[] | string | undefined | null;
export type ClaimFilterCallback = (element: string) => boolean;
export type TokenLike = Record<string, unknown> & {
  ver: number;
  sub: string;
  last_name?: string;
  first_name?: string;
  email: string;
  scp: string[];
  authz_nrc_centers?: string[];
  authz_nrc_site?: string[];
  authz_beconnected_site?: string[];
};
export type ExtractedClaimSet = {
  authz_site?: string[];
  authz_center?: string[];
  authz_region?: string[];
};

const sitecoreCmRoleName = `sitecore-cm`;

const ImpersonationRequestState: unique symbol = Symbol('ImpersonationRequestState');
type RequestWithImpersonationState = NextRequest & {
  nextauth?: {
    [ImpersonationRequestState]?: {
      original: JWT;
      impersonate: JWT | null;
      isImpersonated: boolean;
    };
  };
};

type ImpersonatableRequest = GetServerSidePropsContext['req'] | NextRequest | NextApiRequest;

/**
 * Represents the type for admin roles.
 * It can be either an `AnnouncementAudienceType` or the string literal 'ALL'.
 */
export type AdminRoleType = AnnouncementAudienceType | 'ALL';

export type AdminClaimSet = Record<AdminRoleType | 'ANY', boolean>;

const setImpersonationState = <R extends boolean>({
  req,
  original,
  impersonate,
}: {
  req: ImpersonatableRequest;
  original: R extends true ? string : JWT | null;
  impersonate?: JWT | null;
}): R extends true ? string : JWT | null => {
  const willImpersonate = !!impersonate;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let nextauth = (req as any).nextauth;
  if (!nextauth) {
    nextauth = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (req as any).nextauth = nextauth;
  }
  const work = nextauth;
  if (!work[ImpersonationRequestState]) {
    const state = {
      original,
      isImpersonated: willImpersonate,
      impersonate,
    };
    work[ImpersonationRequestState] = state;
  } else if (work[ImpersonationRequestState].isImpersonated !== willImpersonate) {
    logError(
      'auth-util: impersonate',
      makeLoggableError({
        message: 'Impersonation state mismatch',
        error: new Error('Impersonation state mismatch'),
      })
    );
  }
  return willImpersonate ? (impersonate as R extends true ? string : JWT | null) : original;
};

type ImpersonationStateCheck =
  | {
      hasState: true;
      stateResult: JWT | null;
    }
  | {
      hasState: false;
    };

export const checkImpersonationState = (req: ImpersonatableRequest): ImpersonationStateCheck => {
  if (!('nextauth' in req)) {
    return { hasState: false };
  }
  const work = req as RequestWithImpersonationState;
  if (!!work.nextauth?.[ImpersonationRequestState]) {
    const { isImpersonated, original, impersonate } = work.nextauth[ImpersonationRequestState];
    return {
      hasState: true,
      stateResult: isImpersonated ? impersonate : original,
    };
  }
  return { hasState: false };
};
interface ExtractViewAllOverload {
  /**
   * Extracts the "view all" permissions from a JWT token and returns a record
   * of admin role types with their corresponding boolean values indicating
   * whether the permission is granted.
   *
   * @param token - The JWT token containing authorization information.
   * @returns A record of admin role types with boolean values indicating
   *          whether the "view all" permission is granted for each role.
   */
  (token: JWT | null): AdminClaimSet;
  /**
   * Extracts the "view all" permissions from an array containing center
   * role names that have previously been extracted from a JWT token.
   *
   * @param centerRoles - An array or read-only array containing string
   * values representing center role names.
   * @returns A record of admin role types with boolean values indicating
   *          whether the "view all" permission is granted for each role.
   */
  (centerRoles: Array<string> | ReadonlyArray<string> | null): AdminClaimSet;

  /**
   * Extracts the "view all" permissions from a null value and returns
   * a record of admin role types with all values set to false.
   * @param noRoles - null
   * @returns A record of admin role types with all values set to false.
   */
  (noRoles: null): AdminClaimSet;
}
/**
 * Checks if the given token is a JWT (JSON Web Token).
 *
 * A token is considered a JWT if it is an object and contains at least one of the
 * known properties we assign to a JWT.
 *
 * @param token - The token to check.
 * @returns `true` if the token is a JWT, otherwise `false`.
 */
export const isJwt = (token: unknown): token is JWT =>
  !!token &&
  typeof token === 'object' &&
  ('authz_site' in token ||
    'authz_center' in token ||
    'authz_region' in token ||
    'iat' in token ||
    'email' in token ||
    'exp' in token ||
    'sub' in token ||
    'jti' in token ||
    'iss' in token ||
    'aud' in token ||
    'authz_roles' in token ||
    'authz_claims' in token ||
    'authz_groups' in token);
/**
 * Extracts the "view all" permissions from a JWT token and returns a record
 * of admin role types with their corresponding boolean values indicating
 * whether the permission is granted.
 *
 * @param token - The JWT token containing authorization information.
 * @returns A record of admin role types with boolean values indicating
 *          whether the "view all" permission is granted for each role.
 */
export const extractViewAll: ExtractViewAllOverload = (
  tokenOrRoleString: JWT | Array<string> | ReadonlyArray<string> | null
): AdminClaimSet => {
  if (!tokenOrRoleString) {
    return AnnouncementAudienceTypeValues.reduce(
      (acc: Partial<AdminClaimSet>, aud: keyof Partial<AdminClaimSet>) => {
        acc[aud] = false;
        return acc;
      },
      { ALL: false, ANY: false } as Partial<AdminClaimSet>
    ) as Required<AdminClaimSet>;
  }
  if (isJwt(tokenOrRoleString)) {
    return extractViewAll(tokenOrRoleString.authz_center ?? []);
  }
  return AnnouncementAudienceTypeValues.reduce(
    (map, center: keyof Partial<AdminClaimSet>) => {
      const work = map;
      if (work.ALL === true) {
        work[center] = true;
      } else if (map[center] === undefined) {
        work[center] = false;
      }
      return map;
    },
    [...(tokenOrRoleString ?? [])].reduce((map, center) => {
      const match = /view\sall\s(network|\w{2,3})(?:\ssite)?/i.exec(center);
      const work = map;
      if (match) {
        if (match[1].toLowerCase() === 'network') {
          // global admin
          work.ALL = true;
        } else {
          work[match[1] as AdminRoleType] = true;
        }
        work.ANY = true;
      }
      return map;
    }, {} as Partial<AdminClaimSet>)
  ) as Required<AdminClaimSet>;
};
const impersonationCookieName = () =>
  process.env.NEXTAUTH_URL?.startsWith('https://') ?? env.VERCEL
    ? '__Secure-next-auth-imp.session-token'
    : 'next-auth-imp.session-token';
export const processTokenImpersonation = async (
  req: ImpersonatableRequest,
  userToken: JWT | null
) => {
  // Impersonation is only considered for users with view-all access
  const viewAll = userToken ? extractViewAll(userToken) : { ALL: false, ANY: false };
  if (!viewAll.ALL) {
    return setImpersonationState<false>({ req, original: userToken });
  }
  // If we made it this far we are authorized to perform impersonation; first check in impersonation state
  const resultInState = checkImpersonationState(req);
  if (resultInState.hasState) {
    return resultInState.stateResult;
  }
  // Nothing in state means we need to look closer.  We are building on top of NextAuth's implemenation here -
  // if it's good enough to keep the primaruy session safe, it's good enough for the impersonated one.
  const {
    secureCookie = process.env.NEXTAUTH_URL?.startsWith('https://') ?? !!process.env.VERCEL,
    secret = env.NEXTAUTH_SECRET,
    logger = console,
  } = {};
  const impCookieName = impersonationCookieName();
  const sessionStore = new SessionStore(
    { name: impCookieName, options: { secure: secureCookie } },
    { cookies: req.cookies, headers: req.headers },
    logger
  );
  // Attempt to extract from session store
  const impersonateToken = sessionStore.value;
  if (!impersonateToken) {
    // Nothing - regular ol' non-impersonated user
    return setImpersonationState<false>({ req, original: userToken });
  }
  /*
   We have ourselves an imposter!  Note currently we're only letting Network Website View All
   impersonate, but long-term we'd want to validate users aren't trying to impersonate more than
   they should
  */
  try {
    const decodedImpersonate = await decode({ token: impersonateToken, secret: secret });

    return setImpersonationState<false>({
      req,
      original: userToken,
      impersonate: decodedImpersonate,
    });
  } catch (error) {
    logWarning(
      'An unexpected failure occurred decoding an impersonate token; something fishy is going on - impersonation is disabled for this request.',
      {
        error,
      }
    );
  }

  return setImpersonationState<false>({ req, original: userToken });
};

export const applyImpersonationToken = async ({
  req,
  res,
  impersonate: impersonateFromProps,
  current: currentFromProps,
}: {
  req: ImpersonatableRequest;
  res: NextApiResponse;
  impersonate: JWT | null;
  current?: JWT | null;
}) => {
  const {
    secureCookie = env.NEXTAUTH_URL?.startsWith('https://') ?? env.VERCEL,
    secret = env.NEXTAUTH_SECRET,
    logger = console,
  } = {};
  const impCookieName = impersonationCookieName();
  const sessionStore = new SessionStore(
    {
      name: impCookieName,
      options: { secure: secureCookie, path: '/', httpOnly: true, sameSite: true },
    },
    { cookies: req.cookies, headers: req.headers },
    logger
  );
  let cookieValues: Cookie[] = [];
  if (impersonateFromProps == null) {
    // We always want to be able to unimpersonate
    cookieValues = sessionStore.chunk('', {});
    if ('nextauth' in req) {
      const work = req as RequestWithImpersonationState;
      if (!!work?.nextauth) {
        work.nextauth[ImpersonationRequestState] = undefined;
      }
    }
  } else {
    const isViewAll = extractViewAll(currentFromProps ?? null);
    if (!isViewAll.ALL) {
      throw new Error('Unauthorized activity detected');
    }
    const encoded = await encode({ token: impersonateFromProps, secret: secret as string });

    cookieValues = sessionStore.chunk(encoded, { secure: secureCookie });
  }
  const allCookies = cookieValues.reduce(
    (cookies, cookie) => {
      const { name, value, options } = cookie;
      cookies.push(serialize(name, value, options).toString());
      return cookies;
    },
    (() => {
      const initialCookies: Array<string> = [];
      if (res.hasHeader('Set-Cookie')) {
        const existing = res.getHeader('Set-Cookie');
        if (existing) {
          if (Array.isArray(existing)) {
            existing.forEach((c) => initialCookies.push(c));
          } else {
            initialCookies.push(String(existing));
          }
        }
      }
      return initialCookies;
    })()
  );
  res.setHeader('Set-Cookie', allCookies);
};

export const getToken = async <R extends boolean = false>(
  params: GetTokenParams<R>
): Promise<R extends true ? string : JWT | null> => {
  const { req } = params;
  if (!req) {
    throw new Error('Request object is required');
  }
  // First get a regular ol' token
  const userToken = await getNextAuthToken<R>(params);
  if (!userToken) {
    return userToken;
  }
  // No impersonation support for raw tokens
  if (params.raw === false || typeof userToken === 'string') {
    return userToken;
  }
  return (await processTokenImpersonation(req, userToken)) as R extends true ? string : JWT | null;
};

/**
 * A callback function that region claims out of the site access list.
 *
 * @param element - The claim string to be tested.
 * @returns A boolean indicating whether the element matches the region claim pattern.
 */
const filterRegionClaimFromSite: ClaimFilterCallback = (element: string): element is Claim =>
  !/^[lp]1_nrc_region_.*$/i.test(element);

/**
 * A callback function that filters claims based on specific region patterns.
 *
 * @param element - The claim string to be tested.
 * @returns A boolean indicating whether the element matches the region claim pattern.
 */
const filterRegionClaim: ClaimFilterCallback = (element: string): element is Claim =>
  /^(Non\s)?United\sStates|[lp]1_nrc_region_mexico$/i.test(element);

/**
 * Filters a claim based on a specific pattern.
 *
 * This function checks if the given element matches a predefined regular expression pattern.
 * The pattern includes various claim types such as AC, C[COB], DC, FN, RG, RC[OB]?, TC, and "View All".
 * It ensures that the element contains at least one digit and can include alphanumeric characters and hyphens.
 *
 * @param element - The string element to be tested against the claim pattern.
 * @returns A boolean indicating whether the element matches the claim pattern.
 */
const filterCenterClaim: ClaimFilterCallback = (element: string): element is Claim =>
  /((AC|C[COB]|DC|FN|R(?:G|C[OB]?)|TC)((?=.*\d)[\w-]+)|View\sAll.*)$/gm.test(element);

/**
 * Filters and normalizes a set of claims
 * @param {ClaimSet | null | undefined} claims source claime array to filter.
 * @param {ClaimFilterCallback | undefined} filter Callback used to filter itmes out of the claim set.
 * @returns {ClaimSet | undefined} A filtered claimset, or undefined if no claims are found in @see filter.
 */
const filterClaims = (claims?: ClaimSet | null | string, filter?: ClaimFilterCallback) => {
  // If claims is null/undefined then early exit
  if (!claims) {
    return undefined;
  }
  const ret = (typeof claims === 'string' ? [claims] : claims)
    .map((element) => element?.trim())
    .filter((element: string) => !!element && (filter ?? (() => true))(element));
  return ret.length ? ret : undefined;
};

export const extractWebsiteClaims = (token: TokenLike): ExtractedClaimSet => {
  const decodedGroupClaims = token[env.OKTA_CLAIM_CENTERS] as string[];
  const result = {} as ExtractedClaimSet;
  // Add website claims
  result.authz_site = filterClaims(token[env.OKTA_CLAIM] as string[], filterRegionClaimFromSite);
  result.authz_center = filterClaims(decodedGroupClaims, filterCenterClaim);
  result.authz_region = filterClaims(decodedGroupClaims, filterRegionClaim);
  return result;
};

/**
 * Builds a role string based on the provided token, secret, and skipAnon flag.
 *
 * @param {Object} params - The parameters for building the role string.
 * @param {JWT | null | undefined} params.token - The JSON Web Token (JWT) used for authentication.
 * @param {string | null} [params.secret] - An optional secret string used to determine if the request is from Sitecore CM.
 * @param {boolean} [params.skipAnon] - An optional flag to skip adding the anonymous role.
 * @returns {string} A pipe-delimited string of roles.
 *
 * @remarks
 * - If `secret` matches the `JSS_EDITING_SECRET` environment variable, the role is set to `sitecoreCmRoleName`.
 * - If authentication is disabled (`SKIP_AUTHENTICATION`), the role is set to `sitecoreCmRoleName`.
 * - If `ANONYMOUS_ROLE` is enabled and the request is not from Sitecore CM, the anonymous role is added.
 * - If no roles are determined, a non-existent role `no-access` is returned to force a 403 error.
 * - The roles are sorted alphabetically to ensure a consistent order.
 */
export const buildRoleString = ({
  token,
  secret,
  skipAnon,
}: {
  token: JWT | null | undefined;
  secret?: string | null;
  skipAnon?: boolean;
}): string => {
  const isSitecoreCM = secret === env.JSS_EDITING_SECRET;
  // Get list of roles.
  let role = isSitecoreCM ? [sitecoreCmRoleName] : (token?.authz_site as string[]) || [];
  // If authentication is disabled use the global CM role
  if (env.SKIP_AUTHENTICATION) {
    role = [sitecoreCmRoleName];
  } else if (env.ANONYMOUS_ROLE && !isSitecoreCM) {
    /* If anonymous role is enabled and we are authenticated then add a cache-busting bit... */
    if (token && skipAnon !== true) {
      role.push('auth_anon');
    }
    // And add the anonymous role
    role.push(env.ANONYMOUS_ROLE);
  }
  if (!role?.length) {
    // Instead of blowing up, return a non-existent role to force a 403.
    role = ['no-access'];
  }
  //  Sort to ensure consistent order. JS arrays sort in place.
  role.sort((a, b) => a.localeCompare(b));

  // Combine roles into pipe delimited string
  const ret = role.join('|');
  // Return the role string
  return ret;
};

/**
 * Checks if the given roles include the Sitecore Content Manager role.
 *
 * @param roles - The roles to check, which can be a string or an array of strings.
 * @returns `true` if the roles include the Sitecore Content Manager role, otherwise `false`.
 */
export const isSitecoreContentManager = (roles: RoleStringType): boolean => {
  if (!roles) {
    return false;
  }
  const role =
    typeof roles === 'string'
      ? roles
      : // false is never equal to sitecoreCmRoleName
        roles.length === 1 && roles[0];
  return role === sitecoreCmRoleName;
};

/**
 * Determines if the user is anonymous based on their roles.
 *
 * @param roles - The roles assigned to the user. Can be a single role or an array of roles.
 * @returns `true` if the user is anonymous, `false` otherwise.
 *
 * The function considers a user anonymous if:
 * - No roles are provided.
 * - An empty array of roles is provided.
 * - A single role is provided that matches the anonymous role defined in the environment variables.
 */
export const isAnonymousUser = (roles: RoleStringType): boolean => {
  // If we have no roles we are anon
  if (!roles) {
    return true;
  }
  // Normalize input
  let checkRoles: string[];
  if (roles && Array.isArray(roles)) {
    checkRoles = roles;
  } else {
    checkRoles = [roles];
  }
  // If we have no roles or one and only one role that matches the anon role
  if (
    checkRoles.length === 0 ||
    (checkRoles.length === 1 && checkRoles[0] === env.ANONYMOUS_ROLE)
  ) {
    // Then we are anon
    return true;
  }
  // Otherwise we are some kind of user
  return false;
};

/**
 * Retrieves a list of known role combinations.
 *
 * This function checks the `MEDIA_API_KEYS` environment variable and returns
 * its keys as an array of strings. Additionally, it includes the 'sitecore-cm'
 * role in the returned array.
 *
 * @returns {string[]} An array of known role combinations.
 */
export const getKnownRoleCombinations = () => {
  const roles = env.MEDIA_API_KEYS ? Object.keys(env.MEDIA_API_KEYS) : [];
  return [...roles, 'sitecore-cm'] as string[];
};

/**
 * Retrieves a list of known distinct roles.
 *
 * This function calls `getKnownRoleCombinations` to get an array of role combinations,
 * splits each combination by the '|' character, and then filters out duplicate roles
 * to return a list of unique roles.
 *
 * @returns {string[]} An array of unique role strings.
 */
export const getKnownDistinctRoles = () =>
  getKnownRoleCombinations()
    .flatMap((x) => x.split('|'))
    .filter((v, i, a) => a.indexOf(v) === i);

/**
 * Checks if the provided token has the 'view all' permission for the given role(s).
 *
 * @param token - The JWT token to check.
 * @param role - The role or array of roles to check against. If a single role is provided, it is converted to an array.
 * @returns `true` if the token has the 'view all' permission for all provided roles, otherwise `false`.
 */
export const isViewAll = ({
  token,
  role,
  claimSet = extractViewAll(token),
}: {
  token: JWT;
  role: AdminRoleType | Array<AdminRoleType> | ReadonlyArray<AdminRoleType>;
  claimSet?: AdminClaimSet;
}): boolean => {
  let checkRole: Array<AdminRoleType>;
  if (Array.isArray(role)) {
    // Standard array, can just use it
    checkRole = role;
  } else if (!role || typeof role === 'string') {
    // String or null, need to arrayify it
    checkRole = [role ?? 'ALL'];
  } else {
    // read only arrays are apparently not arrays?  spread it
    checkRole = [...role];
  }
  return checkRole.every((r) => claimSet[r]);
};
